import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, FileText, Download, Clock, User, DollarSign, AlertTriangle } from 'lucide-react';
import { useMedRequestDetail, useApproveMedRequest, useRejectMedRequest, getStatusLabel, getStatusColor, getReasonLabel, getPriorityLabel } from '../../../hooks/useMed';

const statusBadgeClasses: Record<string, string> = {
  yellow: 'bg-yellow-500/20 text-yellow-400',
  blue: 'bg-blue-500/20 text-blue-400',
  green: 'bg-green-500/20 text-green-400',
  red: 'bg-red-500/20 text-red-400',
  gray: 'bg-surface-600 text-surface-300',
};

export function MedReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: request, isLoading } = useMedRequestDetail(id || '');
  const approveMutation = useApproveMedRequest();
  const rejectMutation = useRejectMedRequest();

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [notes, setNotes] = useState('');

  const handleApprove = async () => {
    if (!id) return;
    try {
      await approveMutation.mutateAsync({ id, notes });
      navigate('/admin/compliance/med');
    } catch (err) {
      console.error('Erro ao aprovar:', err);
    }
  };

  const handleReject = async () => {
    if (!id || !rejectReason) return;
    try {
      await rejectMutation.mutateAsync({ id, reason: rejectReason, notes });
      setShowRejectModal(false);
      navigate('/admin/compliance/med');
    } catch (err) {
      console.error('Erro ao rejeitar:', err);
    }
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('pt-BR');
  const formatDateTime = (dateStr: string) => new Date(dateStr).toLocaleString('pt-BR');
  const formatCurrency = (cents: number) => (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-surface-600" />
        <p className="text-surface-400">Solicitação não encontrada</p>
      </div>
    );
  }

  const canReview = ['PENDING', 'UNDER_REVIEW'].includes(request.status);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-surface-700 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">Análise de Solicitação MED</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadgeClasses[getStatusColor(request.status)] || statusBadgeClasses.gray}`}>
              {getStatusLabel(request.status)}
            </span>
          </div>
          <p className="text-surface-400 text-sm mt-1">ID: {request.id}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2"><User className="w-4 h-4 text-primary-400" /> Solicitante</h3>
            <div className="bg-surface-800 rounded-lg p-4 space-y-2">
              <div><span className="text-surface-400 text-sm">Nome:</span> <span className="ml-2">{request.userName}</span></div>
              <div><span className="text-surface-400 text-sm">Data:</span> <span className="ml-2">{formatDateTime(request.createdAt)}</span></div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-400" /> Valores</h3>
            <div className="bg-surface-800 rounded-lg p-4 space-y-2">
              <div><span className="text-surface-400 text-sm">Original:</span> <span className="ml-2">{formatCurrency(request.originalAmount)}</span></div>
              <div><span className="text-surface-400 text-sm">Solicitado:</span> <span className="ml-2 font-bold text-lg">{formatCurrency(request.requestedAmount)}</span></div>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <h3 className="font-semibold">Detalhes da Solicitação</h3>
          <div className="bg-surface-800 rounded-lg p-4 space-y-2">
            <div><span className="text-surface-400 text-sm">Motivo:</span> <span className={`ml-2 ${request.reason === 'FRAUD' ? 'text-red-400 font-medium' : ''}`}>{getReasonLabel(request.reason)}</span></div>
            <div><span className="text-surface-400 text-sm">Prioridade:</span> <span className="ml-2">{getPriorityLabel(request.priority)}</span></div>
            <div><span className="text-surface-400 text-sm">Transação Original:</span> <code className="ml-2 text-sm bg-surface-700 px-2 py-0.5 rounded">{request.originalTransactionId}</code></div>
            <div><span className="text-surface-400 text-sm">Data da Transação:</span> <span className="ml-2">{formatDate(request.originalTransactionDate)}</span></div>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <h3 className="font-semibold">Descrição</h3>
          <div className="bg-surface-800 rounded-lg p-4">
            <p className="whitespace-pre-wrap text-surface-300">{request.description}</p>
          </div>
        </div>

        {/* Documents */}
        {request.documents?.length > 0 && (
          <div className="mt-6 space-y-4">
            <h3 className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-primary-400" /> Documentos ({request.documents.length})</h3>
            <div className="space-y-2">
              {request.documents.map((doc: any) => (
                <div key={doc.id} className="flex items-center justify-between bg-surface-800 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-surface-400" />
                    <span>{doc.fileName}</span>
                    <span className="text-sm text-surface-500">({(doc.fileSize / 1024).toFixed(1)} KB)</span>
                  </div>
                  <button className="text-primary-400 hover:text-primary-300 transition-colors"><Download className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status History */}
        {request.statusHistory?.length > 0 && (
          <div className="mt-6 space-y-4">
            <h3 className="font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-yellow-400" /> Histórico</h3>
            <div className="space-y-3">
              {request.statusHistory.map((h: any) => (
                <div key={h.id} className="flex items-center gap-4 text-sm">
                  <span className="text-surface-500 min-w-[140px]">{formatDateTime(h.createdAt)}</span>
                  <span className="text-surface-300">{h.previousStatus ? `${getStatusLabel(h.previousStatus)} → ` : ''}{getStatusLabel(h.newStatus)}</span>
                  <span className="text-surface-500">por {h.changedByName}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {canReview && (
          <div className="mt-8 pt-6 border-t border-surface-800 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Observações da Análise</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Adicione observações sobre a análise..."
              />
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={rejectMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                <XCircle className="w-5 h-5" /> Rejeitar
              </button>
              <button
                onClick={handleApprove}
                disabled={approveMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                <CheckCircle className="w-5 h-5" /> Aprovar Devolução
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-surface-800 rounded-xl p-6 max-w-md w-full mx-4 border border-surface-700">
            <h3 className="text-lg font-bold mb-4">Rejeitar Solicitação</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Motivo da Rejeição *</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Informe o motivo da rejeição..."
                required
              />
            </div>
            <div className="flex gap-4">
              <button onClick={() => setShowRejectModal(false)} className="flex-1 px-4 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg transition-colors">Cancelar</button>
              <button
                onClick={handleReject}
                disabled={!rejectReason || rejectMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MedReview;
